/*
 * faq-drawer.js
 * ---------------------------------------------------------------------------
 * Controller for the slide-in FAQ drawer (snippets/faq-drawer.liquid).
 * A single native <dialog class="faq-drawer"> per page, opened by any element
 * carrying [data-faq-open="<id>"] (the PDP chip marquee, the /pages/faq
 * questions). Shows one FAQ at a time; slides in from the right; full nav
 * (jump dropdown + Prev/Next); animated, scroll-locking close on the button,
 * ESC and backdrop click. Focus moves into the panel on open and returns to
 * the trigger on close.
 * ---------------------------------------------------------------------------
 */
(function () {
  var dialog = document.querySelector('dialog.faq-drawer');
  if (!dialog || dialog.dataset.faqInit) return;
  dialog.dataset.faqInit = '1';

  var CLOSE_MS = 450; // keep in sync with the transform transition in faq-drawer.css
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var items = Array.from(dialog.querySelectorAll('.faq-drawer__item'));
  var order = items.map(function (el) { return el.dataset.id; });
  var jumps = Array.from(dialog.querySelectorAll('.faq-drawer__jump'));
  var dropdownToggle = dialog.querySelector('.faq-drawer__dropdown-toggle');
  var jumplist = dialog.querySelector('.faq-drawer__jumplist');
  var prevBtn = dialog.querySelector('[data-faq-prev]');
  var nextBtn = dialog.querySelector('[data-faq-next]');
  var closeBtn = dialog.querySelector('.faq-drawer__close');

  var currentIndex = 0;
  var lastTrigger = null;
  var closing = false;

  function setActive(index) {
    if (index < 0 || index >= order.length) return;
    currentIndex = index;
    items.forEach(function (el, i) { el.classList.toggle('is-active', i === index); });
    jumps.forEach(function (btn, i) {
      var on = i === index;
      btn.setAttribute('aria-current', on ? 'true' : 'false');
      btn.hidden = on; // the open question is omitted from the jump list
    });
    if (prevBtn) prevBtn.disabled = index === 0;
    if (nextBtn) nextBtn.disabled = index === order.length - 1;
    var content = dialog.querySelector('.faq-drawer__content');
    if (content) content.scrollTop = 0;
  }

  function openDrawer(id, trigger) {
    var index = order.indexOf(id);
    if (index === -1) index = 0;
    lastTrigger = trigger || null;
    setActive(index);
    closeJumplist();

    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    // Lock background scroll using the theme's shared helper (base.css .overflow-hidden),
    // the same class cart-drawer.js / details-modal.js use — showModal() alone doesn't
    // stop the page behind the drawer from scrolling on mobile.
    document.body.classList.add('overflow-hidden');

    if (reduceMotion) {
      dialog.classList.add('is-open');
    } else {
      // next frame so the transform transition runs from the parked position
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { dialog.classList.add('is-open'); });
      });
    }
    if (closeBtn) closeBtn.focus();
  }

  function finishClose() {
    if (dialog.open && typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
    closing = false;
    if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
    lastTrigger = null;
  }

  function closeDrawer() {
    if (closing) return;
    closing = true;
    closeJumplist();
    dialog.classList.remove('is-open');
    if (reduceMotion) finishClose();
    else window.setTimeout(finishClose, CLOSE_MS);
  }

  function openJumplist() {
    if (!jumplist || !dropdownToggle) return;
    jumplist.hidden = false;
    dropdownToggle.setAttribute('aria-expanded', 'true');
  }
  function closeJumplist() {
    if (!jumplist || !dropdownToggle) return;
    jumplist.hidden = true;
    dropdownToggle.setAttribute('aria-expanded', 'false');
  }

  // ---- Wiring ----

  // Any trigger anywhere on the page: chips, FAQ-page questions, etc.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-faq-open]');
    if (!trigger) return;
    event.preventDefault();
    openDrawer(trigger.dataset.faqOpen, trigger);
  });

  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
  if (prevBtn) prevBtn.addEventListener('click', function () { setActive(currentIndex - 1); });
  if (nextBtn) nextBtn.addEventListener('click', function () { setActive(currentIndex + 1); });

  if (dropdownToggle) {
    dropdownToggle.addEventListener('click', function () {
      if (jumplist && jumplist.hidden) openJumplist();
      else closeJumplist();
    });
  }
  // Jump button i opens panel i: faq-entries.liquid renders both lists from the
  // same blocks, in the same order, with the same skips.
  jumps.forEach(function (btn, i) {
    btn.addEventListener('click', function () { setActive(i); closeJumplist(); });
  });

  // Backdrop click: the dialog element itself receives clicks on its ::backdrop.
  dialog.addEventListener('click', function (event) {
    if (event.target === dialog) closeDrawer();
  });

  // ESC: native <dialog> fires 'cancel'. Intercept so it runs the animated close
  // and the scroll-lock is always released.
  dialog.addEventListener('cancel', function (event) {
    event.preventDefault();
    closeDrawer();
  });

  // Close the jump list on outside click within the dialog.
  dialog.addEventListener('click', function (event) {
    if (jumplist && !jumplist.hidden &&
        !event.target.closest('.faq-drawer__dropdown')) {
      closeJumplist();
    }
  });

  // Deep link: #faq opens the drawer at the first entry, #faq=<entry-id> at
  // that entry (unknown ids fall back to the first, same as any trigger).
  // The hash is left in place — harmless, and back/forward stay sane.
  if (location.hash === '#faq' || location.hash.indexOf('#faq=') === 0) {
    openDrawer(location.hash.slice(5), null);
  }
})();
